import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Activity,
  ArrowRight,
  BarChart3,
  Building2,
  CheckCircle2,
  Clock3,
  Network,
  ShieldCheck,
  Target,
  UserCog,
  Users,
} from 'lucide-react';
import {
  Area,
  AreaChart,
  CartesianGrid,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { format, formatDistanceToNow } from 'date-fns';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import {
  ORGANISATION_ROLE_LABELS,
  type OrganisationRole,
  useOrganisation,
} from '@/contexts/OrganisationContext';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { cn } from '@/lib/utils';

interface EnterpriseDashboardData {
  generated_at: string;
  window_days: number;
  scope: {
    role: OrganisationRole;
    title: string;
    global_company_name: string;
    branch_names: string[];
    unit_names: string[];
  };
  metrics: {
    branches: number;
    units: number;
    people: number;
    managers: number;
    active_learners: number;
    total_sessions: number;
    submitted_sessions: number;
    average_score: number;
    pending_reviews: number;
    total_minutes: number;
  };
  trend: Array<{ activity_date: string; sessions: number; average_score: number | null }>;
  branches: Array<{
    id: string;
    name: string;
    country: string | null;
    unit_count: number;
    people_count: number;
    manager_count: number;
    sessions: number;
    average_score: number | null;
    last_activity: string | null;
  }>;
  units: Array<{
    id: string;
    name: string;
    code: string | null;
    branch_id: string;
    branch_name: string;
    people_count: number;
    manager_count: number;
    sessions: number;
    average_score: number | null;
    pending_reviews: number;
    last_activity: string | null;
  }>;
  people: Array<{
    profile_id: string;
    display_name: string;
    email: string | null;
    role: OrganisationRole;
    branch_name: string | null;
    unit_name: string | null;
    sessions: number;
    submitted_sessions: number;
    average_score: number | null;
    best_score: number | null;
    last_activity: string | null;
    pending_reviews: number;
  }>;
  recent_submissions: Array<{
    id: string;
    evaluation_id: string;
    learner_name: string;
    learner_email: string | null;
    agent_name: string;
    score: number;
    unit_name: string;
    status: 'submitted' | 'viewed' | 'reviewed';
    submitted_at: string;
    can_mark_reviewed: boolean;
  }>;
}

const enterpriseDb = supabase as unknown as {
  rpc: {
    (
      functionName: 'get_enterprise_dashboard',
      args: { p_days: number },
    ): Promise<{ data: unknown; error: { message: string } | null }>;
    (
      functionName: 'get_my_reviewable_submission_ids',
    ): Promise<{ data: Array<{ submission_id: string }> | null; error: { message: string } | null }>;
    (
      functionName: 'mark_evaluation_report_reviewed',
      args: { p_submission_id: string; p_feedback: string },
    ): Promise<{ data: Array<{ status: string; reviewed_at: string; review_feedback: string }> | null; error: { message: string } | null }>;
  };
};
const DATE_WINDOWS = [7, 30, 90] as const;
type DashboardTab = 'overview' | 'structure' | 'people' | 'reviews';

const ROLE_BADGE_STYLES: Record<OrganisationRole, string> = {
  company_admin: 'border-blue-200 bg-blue-50 text-blue-700',
  branch_admin: 'border-cyan-200 bg-cyan-50 text-cyan-700',
  unit_admin: 'border-amber-200 bg-amber-50 text-amber-700',
  unit_manager: 'border-emerald-200 bg-emerald-50 text-emerald-700',
  user: 'border-border bg-muted text-muted-foreground',
};

function formatRelativeDate(value: string | null) {
  if (!value) return 'No activity yet';
  return formatDistanceToNow(new Date(value), { addSuffix: true });
}

function scoreLabel(value: number | null) {
  return value == null ? '—' : `${Math.round(Number(value))}%`;
}

function DashboardSkeleton() {
  return (
    <div className="space-y-6 p-6 lg:p-8">
      <div className="space-y-2">
        <Skeleton className="h-9 w-80" />
        <Skeleton className="h-5 w-[32rem] max-w-full" />
      </div>
      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-6">
        {Array.from({ length: 6 }).map((_, index) => (
          <Skeleton key={index} className="h-28" />
        ))}
      </div>
      <Skeleton className="h-80" />
    </div>
  );
}

function EmptyPanel({ title, description }: { title: string; description: string }) {
  return (
    <div className="flex min-h-48 flex-col items-center justify-center border border-dashed bg-muted/20 px-6 text-center">
      <CheckCircle2 className="mb-3 h-8 w-8 text-muted-foreground" />
      <p className="font-semibold text-foreground">{title}</p>
      <p className="mt-1 max-w-lg text-sm text-muted-foreground">{description}</p>
    </div>
  );
}

export function EnterpriseDashboard() {
  const [days, setDays] = useState<(typeof DATE_WINDOWS)[number]>(30);
  const [activeTab, setActiveTab] = useState<DashboardTab>('overview');
  const [reviewDraft, setReviewDraft] = useState<{ submissionId: string; learnerName: string } | null>(null);
  const [reviewFeedback, setReviewFeedback] = useState('');
  const queryClient = useQueryClient();
  const {
    effectiveRole,
    hasEnterpriseHierarchy,
    isCompanyAdmin,
    isBranchAdmin,
    isUnitAdmin,
    isUnitManager,
  } = useOrganisation();

  const dashboardQuery = useQuery({
    queryKey: ['enterprise-dashboard', days],
    queryFn: async (): Promise<EnterpriseDashboardData> => {
      const [dashboardResult, reviewableResult] = await Promise.all([
        enterpriseDb.rpc('get_enterprise_dashboard', { p_days: days }),
        enterpriseDb.rpc('get_my_reviewable_submission_ids'),
      ]);
      if (dashboardResult.error) throw dashboardResult.error;
      if (reviewableResult.error) throw reviewableResult.error;

      const reviewableIds = new Set((reviewableResult.data || []).map((row) => row.submission_id));
      const dashboard = dashboardResult.data as Omit<EnterpriseDashboardData, 'recent_submissions'> & {
        recent_submissions: Array<Omit<EnterpriseDashboardData['recent_submissions'][number], 'can_mark_reviewed'>>;
      };

      return {
        ...dashboard,
        recent_submissions: dashboard.recent_submissions.map((submission) => ({
          ...submission,
          can_mark_reviewed: reviewableIds.has(submission.id),
        })),
      };
    },
    enabled: hasEnterpriseHierarchy && Boolean(effectiveRole && effectiveRole !== 'user'),
    refetchInterval: 60_000,
  });

  const markReviewedMutation = useMutation({
    mutationFn: async ({ submissionId, feedback }: { submissionId: string; feedback: string }) => {
      const { error } = await enterpriseDb.rpc('mark_evaluation_report_reviewed', {
        p_submission_id: submissionId,
        p_feedback: feedback,
      });
      if (error) throw error;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['enterprise-dashboard'] });
      setReviewDraft(null);
      setReviewFeedback('');
      toast.success('Review completed and feedback shared with the learner.');
    },
    onError: (error) => {
      toast.error(error instanceof Error ? error.message : 'Unable to complete this review.');
    },
  });

  const chartData = useMemo(() => (
    (dashboardQuery.data?.trend || []).map((entry) => ({
      ...entry,
      label: format(new Date(`${entry.activity_date}T00:00:00`), 'd MMM'),
      average_score: entry.average_score == null ? null : Number(entry.average_score),
    }))
  ), [dashboardQuery.data?.trend]);

  if (dashboardQuery.isLoading) return <DashboardSkeleton />;

  if (dashboardQuery.error || !dashboardQuery.data) {
    return (
      <div className="p-6 lg:p-8">
        <Alert variant="destructive">
          <ShieldCheck className="h-4 w-4" />
          <AlertTitle>Dashboard unavailable</AlertTitle>
          <AlertDescription>
            We could not load your organisation scope. Refresh the page or contact support if the issue continues.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  const data = dashboardQuery.data;
  const role = data.scope.role;
  const scopeDescription = isCompanyAdmin
    ? `All branches, units and teams across ${data.scope.global_company_name}`
    : isBranchAdmin
      ? `${data.scope.branch_names.join(', ') || 'Assigned branch'} and its units`
      : `${data.scope.unit_names.join(', ') || 'Assigned unit'} team performance and submitted work`;

  const metrics = [
    {
      label: isCompanyAdmin ? 'Branches' : 'Units',
      value: isCompanyAdmin ? data.metrics.branches : data.metrics.units,
      detail: isCompanyAdmin ? `${data.metrics.units} units` : 'In your scope',
      icon: isCompanyAdmin ? Building2 : Network,
      tone: 'text-blue-700 bg-blue-50',
      tab: 'structure' as DashboardTab,
    },
    {
      label: 'People',
      value: data.metrics.people,
      detail: `${data.metrics.managers} unit managers`,
      icon: Users,
      tone: 'text-cyan-700 bg-cyan-50',
      tab: 'people' as DashboardTab,
    },
    {
      label: 'Sessions',
      value: data.metrics.total_sessions,
      detail: `${data.metrics.active_learners} active learners`,
      icon: Activity,
      tone: 'text-violet-700 bg-violet-50',
      tab: 'overview' as DashboardTab,
    },
    {
      label: 'Average score',
      value: `${Math.round(Number(data.metrics.average_score))}%`,
      detail: `Last ${data.window_days} days`,
      icon: Target,
      tone: 'text-emerald-700 bg-emerald-50',
      tab: 'overview' as DashboardTab,
    },
    {
      label: 'Training time',
      value: `${Number(data.metrics.total_minutes).toLocaleString()}m`,
      detail: 'Recorded utilisation',
      icon: Clock3,
      tone: 'text-amber-700 bg-amber-50',
      tab: 'overview' as DashboardTab,
    },
    {
      label: 'Pending reviews',
      value: data.metrics.pending_reviews,
      detail: isUnitManager ? 'Submitted to you' : 'Within your scope',
      icon: ClipboardReviewIcon,
      tone: data.metrics.pending_reviews ? 'text-orange-700 bg-orange-50' : 'text-slate-600 bg-slate-50',
      tab: 'reviews' as DashboardTab,
    },
  ];

  return (
    <div className="min-h-screen bg-background">
      <div className="mx-auto max-w-[1600px] space-y-6 px-4 py-6 sm:px-6 lg:px-8">
        <header className="flex flex-col gap-4 border-b pb-5 lg:flex-row lg:items-end lg:justify-between">
          <div className="min-w-0">
            <div className="mb-2 flex flex-wrap items-center gap-2">
              <Badge variant="outline" className={cn('font-medium', ROLE_BADGE_STYLES[role])}>
                {ORGANISATION_ROLE_LABELS[role]}
              </Badge>
              <span className="text-sm text-muted-foreground">{data.scope.global_company_name}</span>
            </div>
            <h1 className="text-2xl font-semibold text-foreground sm:text-3xl">{data.scope.title}</h1>
            <p className="mt-1 text-sm text-muted-foreground sm:text-base">{scopeDescription}</p>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <div className="inline-flex h-9 items-center border bg-muted/30 p-1" aria-label="Dashboard date range">
              {DATE_WINDOWS.map((windowDays) => (
                <button
                  key={windowDays}
                  type="button"
                  onClick={() => setDays(windowDays)}
                  className={cn(
                    'h-7 min-w-12 px-3 text-xs font-medium transition-colors',
                    days === windowDays
                      ? 'bg-background text-foreground shadow-sm'
                      : 'text-muted-foreground hover:text-foreground',
                  )}
                >
                  {windowDays}d
                </button>
              ))}
            </div>
            <Button asChild variant="outline" size="sm">
              <Link to="/admin/organisation">Manage organisation <ArrowRight className="ml-2 h-4 w-4" /></Link>
            </Button>
          </div>
        </header>

        <section className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6" aria-label="Organisation metrics">
          {metrics.map((metric) => (
            <button
              key={metric.label}
              type="button"
              onClick={() => setActiveTab(metric.tab)}
              className="group rounded-md border bg-card p-4 text-left transition-colors hover:border-primary/40 hover:bg-muted/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              aria-label={`Open ${metric.label}`}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-xs font-medium uppercase text-muted-foreground">{metric.label}</p>
                  <p className="mt-2 text-2xl font-semibold text-foreground">{metric.value}</p>
                  <p className="mt-1 truncate text-xs text-muted-foreground">{metric.detail}</p>
                </div>
                <div className={cn('flex h-9 w-9 shrink-0 items-center justify-center rounded-md', metric.tone)}>
                  <metric.icon className="h-4 w-4" />
                </div>
              </div>
              <span className="mt-3 inline-flex items-center text-xs font-medium text-primary opacity-70 group-hover:opacity-100">
                Open view <ArrowRight className="ml-1 h-3 w-3" />
              </span>
            </button>
          ))}
        </section>

        <Tabs value={activeTab} onValueChange={(value) => setActiveTab(value as DashboardTab)} className="space-y-4">
          <TabsList className="h-auto w-full justify-start overflow-x-auto rounded-md bg-muted/50 p-1 sm:w-auto">
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="structure">Structure</TabsTrigger>
            <TabsTrigger value="people">People</TabsTrigger>
            <TabsTrigger value="reviews">Reviews</TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="space-y-4">
            <div className="grid gap-4 xl:grid-cols-[minmax(0,1.6fr)_minmax(320px,0.8fr)]">
              <section className="border bg-card p-5">
                <div className="mb-4 flex items-start justify-between gap-4">
                  <div>
                    <h2 className="font-semibold text-foreground">Training activity</h2>
                    <p className="text-sm text-muted-foreground">Sessions and average score in your permitted scope</p>
                  </div>
                  <Badge variant="outline">{data.metrics.submitted_sessions} submitted</Badge>
                </div>
                {chartData.length ? (
                  <div className="h-72 w-full">
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart data={chartData} margin={{ top: 8, right: 8, left: -20, bottom: 0 }}>
                        <defs>
                          <linearGradient id="enterpriseSessions" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="hsl(var(--primary))" stopOpacity={0.28} />
                            <stop offset="95%" stopColor="hsl(var(--primary))" stopOpacity={0.02} />
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="hsl(var(--border))" />
                        <XAxis dataKey="label" tickLine={false} axisLine={false} tick={{ fontSize: 11 }} />
                        <YAxis yAxisId="sessions" allowDecimals={false} tickLine={false} axisLine={false} tick={{ fontSize: 11 }} />
                        <YAxis yAxisId="score" orientation="right" domain={[0, 100]} hide />
                        <Tooltip
                          contentStyle={{ borderRadius: 6, borderColor: 'hsl(var(--border))', fontSize: 12 }}
                          formatter={(value: number, name: string) => [name === 'average_score' ? `${value}%` : value, name === 'average_score' ? 'Average score' : 'Sessions']}
                        />
                        <Area yAxisId="sessions" type="monotone" dataKey="sessions" stroke="hsl(var(--primary))" fill="url(#enterpriseSessions)" strokeWidth={2} />
                        <Line yAxisId="score" type="monotone" dataKey="average_score" stroke="#059669" strokeWidth={2} dot={false} connectNulls />
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>
                ) : (
                  <EmptyPanel title="No activity in this period" description="Completed simulations will appear here as soon as learners begin training." />
                )}
              </section>

              <section className="border bg-card p-5">
                <div className="mb-4">
                  <h2 className="font-semibold text-foreground">Scope health</h2>
                  <p className="text-sm text-muted-foreground">
                    {isCompanyAdmin ? 'Branch performance at a glance' : 'Unit performance at a glance'}
                  </p>
                </div>
                <div className="space-y-1">
                  {(isCompanyAdmin ? data.branches.slice(0, 6) : data.units.slice(0, 6)).map((item) => (
                    <Link
                      key={item.id}
                      to={`/admin/organisation?tab=structure&${isCompanyAdmin ? 'branch' : 'unit'}=${item.id}`}
                      className="group flex items-center justify-between gap-4 border-b py-3 transition-colors hover:bg-muted/30 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring last:border-0"
                    >
                      <div className="min-w-0">
                        <p className="flex items-center gap-1 truncate text-sm font-medium text-foreground">
                          {item.name}<ArrowRight className="h-3 w-3 opacity-0 transition-opacity group-hover:opacity-100" />
                        </p>
                        <p className="text-xs text-muted-foreground">{item.sessions} sessions · {formatRelativeDate(item.last_activity)}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-semibold">{scoreLabel(item.average_score)}</p>
                        <p className="text-xs text-muted-foreground">average</p>
                      </div>
                    </Link>
                  ))}
                  {!(isCompanyAdmin ? data.branches.length : data.units.length) && (
                    <EmptyPanel title="Structure ready" description="Add branches, units and people from Organisation Management." />
                  )}
                </div>
              </section>
            </div>
          </TabsContent>

          <TabsContent value="structure">
            <section className="overflow-hidden border bg-card">
              <div className="border-b px-5 py-4">
                <h2 className="font-semibold text-foreground">Branches and units</h2>
                <p className="text-sm text-muted-foreground">Only hierarchy areas within your assigned scope are shown.</p>
              </div>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Unit</TableHead>
                      <TableHead>Branch</TableHead>
                      <TableHead className="text-right">People</TableHead>
                      <TableHead className="text-right">Managers</TableHead>
                      <TableHead className="text-right">Sessions</TableHead>
                      <TableHead className="text-right">Average</TableHead>
                      <TableHead className="text-right">Reviews</TableHead>
                      <TableHead>Last activity</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {data.units.map((unit) => (
                      <TableRow key={unit.id}>
                        <TableCell>
                          <Link to={`/admin/organisation?tab=structure&unit=${unit.id}`} className="font-medium text-primary hover:underline">
                            {unit.name}
                          </Link>
                          {unit.code && <p className="text-xs text-muted-foreground">{unit.code}</p>}
                        </TableCell>
                        <TableCell>{unit.branch_name}</TableCell>
                        <TableCell className="text-right">{unit.people_count}</TableCell>
                        <TableCell className="text-right">{unit.manager_count}</TableCell>
                        <TableCell className="text-right">{unit.sessions}</TableCell>
                        <TableCell className="text-right font-medium">{scoreLabel(unit.average_score)}</TableCell>
                        <TableCell className="text-right">{unit.pending_reviews}</TableCell>
                        <TableCell className="whitespace-nowrap text-muted-foreground">{formatRelativeDate(unit.last_activity)}</TableCell>
                      </TableRow>
                    ))}
                    {!data.units.length && (
                      <TableRow><TableCell colSpan={8} className="h-32 text-center text-muted-foreground">No units are available in your scope.</TableCell></TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
            </section>
          </TabsContent>

          <TabsContent value="people">
            <section className="overflow-hidden border bg-card">
              <div className="flex flex-col gap-3 border-b px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <h2 className="font-semibold text-foreground">People performance</h2>
                  <p className="text-sm text-muted-foreground">Learners and leaders within your permitted hierarchy.</p>
                </div>
                <Button asChild variant="outline" size="sm"><Link to="/admin/users"><UserCog className="mr-2 h-4 w-4" />Manage people</Link></Button>
              </div>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Person</TableHead>
                      <TableHead>Role</TableHead>
                      <TableHead>Scope</TableHead>
                      <TableHead className="text-right">Sessions</TableHead>
                      <TableHead className="text-right">Submitted</TableHead>
                      <TableHead className="text-right">Average</TableHead>
                      <TableHead>Last activity</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {data.people.map((person) => (
                      <TableRow key={person.profile_id}>
                        <TableCell>
                          <p className="font-medium">{person.display_name}</p>
                          <p className="text-xs text-muted-foreground">{person.email || 'No email recorded'}</p>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className={cn('whitespace-nowrap font-normal', ROLE_BADGE_STYLES[person.role])}>
                            {ORGANISATION_ROLE_LABELS[person.role]}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <p className="text-sm">{person.unit_name || person.branch_name || 'Global company'}</p>
                          {person.unit_name && person.branch_name && <p className="text-xs text-muted-foreground">{person.branch_name}</p>}
                        </TableCell>
                        <TableCell className="text-right">{person.sessions}</TableCell>
                        <TableCell className="text-right">{person.submitted_sessions}</TableCell>
                        <TableCell className="text-right font-medium">{scoreLabel(person.average_score)}</TableCell>
                        <TableCell className="whitespace-nowrap text-muted-foreground">{formatRelativeDate(person.last_activity)}</TableCell>
                      </TableRow>
                    ))}
                    {!data.people.length && (
                      <TableRow><TableCell colSpan={7} className="h-32 text-center text-muted-foreground">No people are assigned to this scope yet.</TableCell></TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
            </section>
          </TabsContent>

          <TabsContent value="reviews">
            <section className="overflow-hidden border bg-card">
              <div className="border-b px-5 py-4">
                <h2 className="font-semibold text-foreground">Submitted simulations</h2>
                <p className="text-sm text-muted-foreground">
                  {isUnitManager ? 'Simulation reports submitted directly to you.' : 'Review activity across the units you manage.'}
                </p>
              </div>
              {data.recent_submissions.length ? (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Learner</TableHead>
                        <TableHead>Simulation</TableHead>
                        <TableHead>Unit</TableHead>
                        <TableHead className="text-right">Score</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Submitted</TableHead>
                        <TableHead className="text-right"><span className="sr-only">Open</span></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {data.recent_submissions.map((submission) => (
                        <TableRow key={submission.id}>
                          <TableCell>
                            <p className="font-medium">{submission.learner_name}</p>
                            <p className="text-xs text-muted-foreground">{submission.learner_email || 'No email recorded'}</p>
                          </TableCell>
                          <TableCell>{submission.agent_name}</TableCell>
                          <TableCell>{submission.unit_name}</TableCell>
                          <TableCell className="text-right font-medium">{Math.round(Number(submission.score))}%</TableCell>
                          <TableCell><Badge variant={submission.status === 'reviewed' ? 'secondary' : 'outline'} className="capitalize">{submission.status}</Badge></TableCell>
                          <TableCell className="whitespace-nowrap text-muted-foreground">{formatRelativeDate(submission.submitted_at)}</TableCell>
                          <TableCell className="text-right">
                            <div className="flex justify-end gap-2">
                              <Button asChild variant="ghost" size="sm">
                                <Link to={`/simulation/results/${submission.evaluation_id}`}>Open report</Link>
                              </Button>
                              {submission.can_mark_reviewed && (
                                <Button
                                  size="sm"
                                  onClick={() => {
                                    setReviewDraft({
                                      submissionId: submission.id,
                                      learnerName: submission.learner_name,
                                    });
                                    setReviewFeedback('');
                                  }}
                                  disabled={markReviewedMutation.isPending}
                                >
                                  Complete review
                                </Button>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              ) : (
                <EmptyPanel title="No reviews waiting" description="Submitted simulation reports will appear here for the appropriate unit manager or administrator." />
              )}
            </section>
          </TabsContent>
        </Tabs>

        {(isUnitAdmin || isUnitManager) && (
          <p className="text-xs text-muted-foreground">
            Data is restricted to your assigned unit scope. Contact a branch administrator if your team assignment needs to change.
          </p>
        )}

        <Dialog
          open={Boolean(reviewDraft)}
          onOpenChange={(open) => {
            if (!open && !markReviewedMutation.isPending) {
              setReviewDraft(null);
              setReviewFeedback('');
            }
          }}
        >
          <DialogContent preventOutsideClose={markReviewedMutation.isPending}>
            <DialogHeader>
              <DialogTitle>Complete manager review</DialogTitle>
              <DialogDescription>
                Share clear, actionable feedback with {reviewDraft?.learnerName || 'the learner'}.
                They will see it with this simulation report.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-2">
              <div className="flex items-center justify-between gap-3">
                <Label htmlFor="manager-review-feedback">Feedback for the learner</Label>
                <span className="text-xs text-muted-foreground">{reviewFeedback.length}/5000</span>
              </div>
              <Textarea
                id="manager-review-feedback"
                value={reviewFeedback}
                onChange={(event) => setReviewFeedback(event.target.value)}
                maxLength={5000}
                rows={7}
                placeholder="Explain what was done well and the next improvement to focus on."
                disabled={markReviewedMutation.isPending}
              />
            </div>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => {
                  setReviewDraft(null);
                  setReviewFeedback('');
                }}
                disabled={markReviewedMutation.isPending}
              >
                Cancel
              </Button>
              <Button
                onClick={() => {
                  if (!reviewDraft) return;
                  markReviewedMutation.mutate({
                    submissionId: reviewDraft.submissionId,
                    feedback: reviewFeedback.trim(),
                  });
                }}
                disabled={!reviewDraft || !reviewFeedback.trim() || markReviewedMutation.isPending}
              >
                {markReviewedMutation.isPending ? 'Sharing feedback…' : 'Complete and share feedback'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}

function ClipboardReviewIcon({ className }: { className?: string }) {
  return <BarChart3 className={className} />;
}
